import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DropdownItem, DropdownMenu, Icon } from "semantic-ui-react";

import { Dropdown, InterventionModal, useCan } from "components";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import "modules/AppointmentDetails/components/CustomerActions/components/CommunicationRemarkActionSelector/CommunicationRemarkActionSelector.scss";
import { useAppointmentDealer, useAppointmentLocation } from "modules/AppointmentDetails/hooks";
import { canWriteBackCustomerData, generateDMSNr } from "utils/appointmentUtils";
import { getExtensionFromURL } from "utils/common";

import type { Appointment, CheckInAttachment, CheckInRemark, DiagnoseOverviewAttachment, DiagnoseOverviewRemark, KeyLockerRemark, KioskRemark } from "models";
import type { AddInterventionPayload } from "modules/AppointmentDetails/hooks";
import type { InclusiveUnion } from "utils/type";

type Remark = InclusiveUnion<CheckInRemark | DiagnoseOverviewRemark | KeyLockerRemark | KioskRemark>;
export type RemarkIdProperty = "check_in_remark_id" | "diagnose_overview_remark_id" | "keylocker_remark_id" | "kiosk_remark_id";

type CommunicationRemarksActionSelectorProps = {
  remark: Remark;
  remarkIdProperty: RemarkIdProperty;
  remarkId: number;
  appointment: Appointment;
  onInterventionConverted: () => void;
};

const addType = (attachments?: (CheckInAttachment | DiagnoseOverviewAttachment)[]) =>
  attachments?.map(a => ({ ...a, visible_to_customer: true, type: getExtensionFromURL(a.url) }));

export const CommunicationRemarkActionSelector = ({
  remark,
  remarkIdProperty,
  remarkId,
  appointment,
  onInterventionConverted
}: CommunicationRemarksActionSelectorProps) => {
  const { t } = useTranslation();

  const [convertToInterventionPayload, setConvertToInterventionPayload] = useState<AddInterventionPayload | null>(null);

  const appointmentLocation = useAppointmentLocation(appointment);
  const appointmentDealer = useAppointmentDealer(appointment);
  const canCreateIntervention = useCan(ACL_ACTIVITIES.Create, ACL_RESOURCES.Interventions);

  const isRemarkAlreadyConvertedToIntervention = appointment.interventions?.some(
    ({ diagnose_overview_remark_id, check_in_remark_id, keylocker_remark_id, kiosk_remark_id }) =>
      [diagnose_overview_remark_id, check_in_remark_id, keylocker_remark_id, kiosk_remark_id].includes(remark.id)
  );
  const canConvertToIntervention = canCreateIntervention && appointmentLocation?.convert_checklist_to_intervention_is_enabled && !isRemarkAlreadyConvertedToIntervention;

  const handleConvertToIntervention = () => {
    setConvertToInterventionPayload({
      dms_nr: generateDMSNr(),
      title: remark.title ?? "",
      appointment_id: appointment.id,
      customer_ok: true,
      description: remark.description,
      price: 0,
      attachments: addType(remark.attachments),
      [remarkIdProperty]: remarkId
    });
  };

  return (
    <>
      <div className="CommunicationRemarkActionSelector">
        <Dropdown
          disabled={!canConvertToIntervention}
          className="action-dropdown"
          floating
          icon={null}
          direction="left"
          trigger={<Icon className={!canConvertToIntervention ? "link horizontal grey" : "plus grey"} />}
        >
          <DropdownMenu className="dropdown-menu">
            {canConvertToIntervention && (
              <DropdownItem onClick={handleConvertToIntervention}>
                <Icon className="screwdriver wrench" />
                {t("v8_convert_to_intervention", "Convert to Intervention")}
              </DropdownItem>
            )}
          </DropdownMenu>
        </Dropdown>
      </div>

      <InterventionModal
        appointmentId={appointment.id}
        carId={appointment.car_id}
        location={appointmentLocation}
        dealer={appointmentDealer}
        intervention={convertToInterventionPayload}
        open={convertToInterventionPayload !== null}
        onClose={() => setConvertToInterventionPayload(null)}
        onInterventionCreated={onInterventionConverted}
        canWriteBackCustomerData={canWriteBackCustomerData(appointmentLocation)}
      />
    </>
  );
};
